import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useSesion } from './lib/sesion';
import { RUTA_CLAVE_NUEVA } from './lib/enlace-clave';
import { cargarPagina, LimiteDeError } from './lib/cargar-pagina';

/**
 * MAPA DE RUTAS — transcripción de MODS / CAT / SUBPGS del monolito v61.
 * La columna `pagina` es el id que vive en auth.pagina y que el backend usa
 * para autorizar. Si un id no está en sesion.paginas, la ruta no se monta.
 *
 * Cada `element` apunta a una página que HAY QUE CONSTRUIR. Las que aún no
 * existen usan <Pendiente/> para que la navegación no se rompa: así el portal
 * es navegable desde el día 1 y se van rellenando módulo por módulo.
 */
const Pendiente = lazy(() => cargarPagina(() => import('./paginas/Pendiente')));
const Login = lazy(() => cargarPagina(() => import('./paginas/Login')));
/* db/106 · La pantalla que canjea el enlace de «¿olvidó su contraseña?». NO
 * lleva `pagina` y no está en RUTAS: no es un módulo del portal, es la segunda
 * puerta de la calle. Se monta en la rama sin sesión, junto al acceso. */
const ClaveNueva = lazy(() => cargarPagina(() => import('./paginas/ClaveNueva')));
const Resumen = lazy(() => cargarPagina(() => import('./paginas/Resumen')));
const Vendedores = lazy(() => cargarPagina(() => import('./paginas/Vendedores')));
const VendedoresEvolucion = lazy(() => cargarPagina(() => import('./paginas/VendedoresEvolucion')));
const VendedoresAnalisis = lazy(() => cargarPagina(() => import('./paginas/VendedoresAnalisis')));
const VendedoresComportamiento = lazy(() => cargarPagina(() => import('./paginas/VendedoresComportamiento')));
const Marcas = lazy(() => cargarPagina(() => import('./paginas/Marcas')));
const MarcaDetalle = lazy(() => cargarPagina(() => import('./paginas/MarcaDetalle')));
const Clientes = lazy(() => cargarPagina(() => import('./paginas/Clientes')));
const ClientesAnalisis = lazy(() => cargarPagina(() => import('./paginas/ClientesAnalisis')));
const ClientesMaestra = lazy(() => cargarPagina(() => import('./paginas/ClientesMaestra')));
const ClientesFicha = lazy(() => cargarPagina(() => import('./paginas/ClientesFicha')));
const ClientesActivacion = lazy(() => cargarPagina(() => import('./paginas/ClientesActivacion')));
const ClientesTendencia = lazy(() => cargarPagina(() => import('./paginas/ClientesTendencia')));
const Financiero = lazy(() => cargarPagina(() => import('./paginas/Financiero')));
const Metas = lazy(() => cargarPagina(() => import('./paginas/Metas')));
const PowerQuery = lazy(() => cargarPagina(() => import('./paginas/PowerQuery')));
const PowerQueryDhv = lazy(() => cargarPagina(() => import('./paginas/PowerQueryDhv')));
const PowerQueryDif = lazy(() => cargarPagina(() => import('./paginas/PowerQueryDif')));
const Cartera = lazy(() => cargarPagina(() => import('./paginas/Cartera')));
const CarteraClientes = lazy(() => cargarPagina(() => import('./paginas/CarteraClientes')));
const CarteraHistorial = lazy(() => cargarPagina(() => import('./paginas/CarteraHistorial')));
const CarteraCxc = lazy(() => cargarPagina(() => import('./paginas/CarteraCxc')));
const CarteraDiasCalle = lazy(() => cargarPagina(() => import('./paginas/CarteraDiasCalle')));
const CarteraCerradas = lazy(() => cargarPagina(() => import('./paginas/CarteraCerradas')));
const ResumenInsights = lazy(() => cargarPagina(() => import('./paginas/ResumenInsights')));
const AnaliticaComparador = lazy(() => cargarPagina(() => import('./paginas/AnaliticaComparador')));
const AnaliticaPareto = lazy(() => cargarPagina(() => import('./paginas/AnaliticaPareto')));
const AnaliticaPuente = lazy(() => cargarPagina(() => import('./paginas/AnaliticaPuente')));
const AnaliticaEstacionalidad = lazy(() => cargarPagina(() => import('./paginas/AnaliticaEstacionalidad')));
const AnaliticaWhatIf = lazy(() => cargarPagina(() => import('./paginas/AnaliticaWhatIf')));
const PortalFtd = lazy(() => cargarPagina(() => import('./paginas/PortalFtd')));
const ExpoMaracaibo = lazy(() => cargarPagina(() => import('./paginas/ExpoMaracaibo')));
const BitacoraFtd = lazy(() => cargarPagina(() => import('./paginas/BitacoraFtd')));
const NcFarmatodo = lazy(() => cargarPagina(() => import('./paginas/NcFarmatodo')));
const NegoFarmatodo = lazy(() => cargarPagina(() => import('./paginas/NegoFarmatodo')));
const Lanzamientos = lazy(() => cargarPagina(() => import('./paginas/Lanzamientos')));
const LanzamientoDetalle = lazy(() => cargarPagina(() => import('./paginas/LanzamientoDetalle')));
const Trade = lazy(() => cargarPagina(() => import('./paginas/Trade')));
const Negociaciones = lazy(() => cargarPagina(() => import('./paginas/Negociaciones')));
const NegociacionesNC = lazy(() => cargarPagina(() => import('./paginas/NegociacionesNC')));
const NegociacionesBBK = lazy(() => cargarPagina(() => import('./paginas/NegociacionesBBK')));
const CarteraPagos = lazy(() => cargarPagina(() => import('./paginas/CarteraPagos')));
const Inactivacion = lazy(() => cargarPagina(() => import('./paginas/Inactivacion')));
const Catalogos = lazy(() => cargarPagina(() => import('./paginas/Catalogos')));
const CatalogosAdmin = lazy(() => cargarPagina(() => import('./paginas/CatalogosAdmin')));
const PasosComerciales = lazy(() => cargarPagina(() => import('./paginas/PasosComerciales')));
const Kpis = lazy(() => cargarPagina(() => import('./paginas/Kpis')));
const ReporteFlash = lazy(() => cargarPagina(() => import('./paginas/ReporteFlash')));
const ReporteActInact = lazy(() => cargarPagina(() => import('./paginas/ReporteActInact')));
const InformacionInteres = lazy(() => cargarPagina(() => import('./paginas/InformacionInteres')));
const InformacionInteresAdmin = lazy(() => cargarPagina(() => import('./paginas/InformacionInteresAdmin')));
const Constructor = lazy(() => cargarPagina(() => import('./paginas/Constructor')));
const GoToMarket = lazy(() => cargarPagina(() => import('./paginas/GoToMarket')));
const ResumenEvolucion = lazy(() => cargarPagina(() => import('./paginas/ResumenEvolucion')));
const ResumenForecast = lazy(() => cargarPagina(() => import('./paginas/ResumenForecast')));
const ResumenDescargas = lazy(() => cargarPagina(() => import('./paginas/ResumenDescargas')));
const Viaticos = lazy(() => cargarPagina(() => import('./paginas/Viaticos')));
const Devoluciones = lazy(() => cargarPagina(() => import('./paginas/Devoluciones')));
const Planificacion = lazy(() => cargarPagina(() => import('./paginas/Planificacion')));
const PlanSeguimiento = lazy(() => cargarPagina(() => import('./paginas/PlanSeguimiento')));
const PlanCumplimiento = lazy(() => cargarPagina(() => import('./paginas/PlanCumplimiento')));
const MarcajeVisitas = lazy(() => cargarPagina(() => import('./paginas/MarcajeVisitas')));
const Promociones = lazy(() => cargarPagina(() => import('./paginas/Promociones')));
const PlanCoordinadores = lazy(() => cargarPagina(() => import('./paginas/PlanCoordinadores')));
const Bitacora = lazy(() => cargarPagina(() => import('./paginas/Bitacora')));
const ProspectosRrss = lazy(() => cargarPagina(() => import('./paginas/ProspectosRrss')));
const Prospecto = lazy(() => cargarPagina(() => import('./paginas/Prospecto')));
const DiamanteCuentasClave = lazy(() => cargarPagina(() => import('./paginas/DiamanteCuentasClave')));
const DiamanteFarmatodo = lazy(() => cargarPagina(() => import('./paginas/DiamanteFarmatodo')));
const DiamanteIvooMultimax = lazy(() => cargarPagina(() => import('./paginas/DiamanteIvooMultimax')));
const InventarioMultimax = lazy(() => cargarPagina(() => import('./paginas/InventarioMultimax')));
const PortafolioGama = lazy(() => cargarPagina(() => import('./paginas/PortafolioGama')));
const GestionClientes = lazy(() => cargarPagina(() => import('./paginas/GestionClientes')));
const InformeCaptacion = lazy(() => cargarPagina(() => import('./paginas/InformeCaptacion')));
const Frecuencia = lazy(() => cargarPagina(() => import('./paginas/Frecuencia')));
const NivelServicioSla = lazy(() => cargarPagina(() => import('./paginas/NivelServicioSla')));
const TrazabilidadMkt = lazy(() => cargarPagina(() => import('./paginas/TrazabilidadMkt')));
const ControlPedidos = lazy(() => cargarPagina(() => import('./paginas/ControlPedidos')));

export interface DefRuta {
  pagina: string;
  ruta: string;
  titulo: string;
  submodulo: string;
  /** true = ruta con parámetro; se monta pero NO aparece en navbar ni sidebar. */
  oculta?: boolean;
}

/** Debe coincidir fila por fila con db/002_seed_rbac.sql → auth.pagina. */
export const RUTAS: DefRuta[] = [
  { pagina: 'resumen',           ruta: '/resumen',                    titulo: 'Resumen ejecutivo',         submodulo: '@emp' },
  { pagina: 'evolucion',         ruta: '/resumen/evolucion',          titulo: 'Evolución',                 submodulo: '@emp' },
  { pagina: 'forecast',          ruta: '/resumen/forecast',           titulo: 'Forecast',                  submodulo: '@emp' },
  { pagina: 'descarga',          ruta: '/resumen/descargas',          titulo: 'Descargas',                 submodulo: '@emp' },
  /* MUDADA desde Analítica el 2026-08-21 (db/050), a pedido de la usuaria: los
   * hallazgos automáticos los ve todo el que tenga Resumen General y no los 4
   * roles con Analítica. El id de la página NO cambia — lo nombran la app móvil,
   * los arneses y los guardas de db/049; lo que cambia es de dónde cuelga. */
  { pagina: 'an-insights',       ruta: '/resumen/insights',           titulo: 'Insights automáticos',      submodulo: '@emp' },
  { pagina: 'vendedor',          ruta: '/vendedores',                 titulo: 'Panel general',             submodulo: 'vend' },
  { pagina: 'vd-evolucion',      ruta: '/vendedores/evolucion',       titulo: 'Evolución',                 submodulo: 'vend' },
  { pagina: 'vd-analisis',       ruta: '/vendedores/analisis',        titulo: 'Análisis',                  submodulo: 'vend' },
  { pagina: 'vd-comportamiento', ruta: '/vendedores/comportamiento',  titulo: 'Comportamiento',            submodulo: 'vend' },
  { pagina: 'marcas',            ruta: '/marcas',                     titulo: 'Marcas',                    submodulo: 'marcas' },
  { pagina: 'marca-detail',      ruta: '/marcas/:marcaId',            titulo: 'Detalle de marca',          submodulo: 'marcas', oculta: true },
  { pagina: 'lanzamientos',      ruta: '/lanzamientos',               titulo: 'Lanzamientos',              submodulo: 'lanz' },
  { pagina: 'lanz-detail',       ruta: '/lanzamientos/:id',           titulo: 'Detalle de lanzamiento',    submodulo: 'lanz', oculta: true },
  { pagina: 'clientes',          ruta: '/clientes',                   titulo: 'Comportamiento general',    submodulo: 'cli' },
  { pagina: 'cli-activacion',    ruta: '/clientes/activacion',        titulo: 'Activación / Inactivación', submodulo: 'cli' },
  { pagina: 'cli-tendencia',     ruta: '/clientes/tendencia',         titulo: 'Tendencia',                 submodulo: 'cli' },
  { pagina: 'cli-analisis',      ruta: '/clientes/analisis',          titulo: 'Análisis',                  submodulo: 'cli' },
  { pagina: 'cli-maestra',       ruta: '/clientes/maestra',           titulo: 'Maestra',                   submodulo: 'cli' },
  /* La ficha de UN cliente (db/234, 2026-10-12): contacto, crédito, activación
   * e inactivación y frecuencia, leídos en vivo de lo que cargan Cobranzas, C.E.I.
   * y el circuito de Inactivación. Misma audiencia que sus hermanas. */
  { pagina: 'cli-ficha',         ruta: '/clientes/ficha',             titulo: 'Ficha de clientes',             submodulo: 'cli' },
  { pagina: 'financiero',        ruta: '/financiero',                 titulo: 'Análisis Financiero',       submodulo: 'fin' },
  { pagina: 'metas',             ruta: '/metas',                      titulo: 'Metas',                     submodulo: 'metas' },
  /* Power Query se divide en dos (db/246, 2026-10-15): un tablero por empresa,
   * cada uno con su base. `pquery` conserva su id y su ruta (es el de DDH). El
   * de Difranca se sumó el 2026-10-26 (db/264). */
  { pagina: 'pquery',            ruta: '/consulta-general',           titulo: 'Power Query DDH',           submodulo: 'pquery' },
  { pagina: 'pquery-dhv',        ruta: '/consulta-general-dhv',       titulo: 'Power Query DHV',           submodulo: 'pquery' },
  { pagina: 'pquery-dif',        ruta: '/consulta-general-dif',       titulo: 'Power Query DIF',           submodulo: 'pquery' },
  /* El Simulador de Promociones vive DENTRO de esta página desde el 2026-08-14
   * (`componentes/SimuladorPromo.tsx`, pestaña «Nueva solicitud»). Su página
   * propia (`nego-sim`, /negociaciones/simulador) se retiró a petición de la
   * usuaria; la ruta vieja redirige en `Rutas()`. El registro `nego-sim` de
   * `auth.pagina` SIGUE VIVO a propósito: es el candado del endpoint
   * `/api/nego-simulador` (db/027 lo cuelga de los mismos módulos que `nego`). */
  { pagina: 'nego',              ruta: '/negociaciones',              titulo: 'Negociaciones Comerciales', submodulo: 'nego' },
  /* PÁGINA NUEVA (2026-08-26, pedida por la usuaria) — Negociaciones
   * Comerciales replicada para los módulos Facturación y Finanzas, con la
   * columna del N° de la nota de crédito. Su submódulo es PROPIO (`nego-nc`) y
   * no `nego`: si compartiera submódulo, el submenú de los otros seis módulos
   * que conceden `nego` ofrecería la réplica en cuanto un usuario tuviera las
   * dos páginas, y lo que se pidió es que esa sección no cambie. Ver
   * db/072_negociacion_nc.sql. */
  { pagina: 'nego-nc',           ruta: '/negociaciones-nc',           titulo: 'Negociaciones · Notas de Crédito', submodulo: 'nego-nc' },
  /* PÁGINA NUEVA (2026-09-29, pedida por la usuaria) — Negociaciones BBK. Su
   * submódulo es PROPIO (`nego-bbk`) por lo mismo que la réplica de la NC: si
   * compartiera `nego`, saldría en los ocho módulos que conceden esa página, y
   * se pidió en Comercial (y en Facturación, que carga la NC aquí). db/190. */
  { pagina: 'nego-bbk',          ruta: '/negociaciones-bbk',          titulo: 'Negociaciones BBK',         submodulo: 'nego-bbk' },
  { pagina: 'trade',             ruta: '/trade',                      titulo: 'Trade',                     submodulo: 'trade' },
  /* SECCIÓN NUEVA (2026-09-23, pedida por la usuaria) — TRAZABILIDAD: el
   * CUADRO_CONTROL_PRESPUESTO MARKETING que lleva Trade Marketing (asignación,
   * control de ejecución y montos de Finanzas, con sus reportes), cargado
   * subiendo el propio libro. Cuelga SOLO de `tmk` y la ven SOLO TRADEMARKETING
   * y los MASTER: db/181 poda nominalmente a los otros once roles del módulo, y
   * el endpoint lo repite como lista positiva. */
  { pagina: 'traz-mkt',          ruta: '/trazabilidad-marketing',     titulo: 'Trazabilidad',              submodulo: 'traz' },
  /* SECCIÓN NUEVA (2026-09-23, pedida por la usuaria) — CONTROL DE PEDIDOS:
   * Facturación sube su libro DSP-FOR-005 y de ahí salen las guías de despacho
   * que imprime Almacén. Cuelga de `fact` (los tres usuarios de Facturación y
   * los MASTER) y de `almacen` (ALMACENYDESPACHO); db/182 poda a los otros
   * cinco roles de `almacen` —asesores y coordinadores incluidos (NORMA 1)—. */
  { pagina: 'ctrl-pedidos',      ruta: '/control-pedidos',            titulo: 'Control de Pedidos',        submodulo: 'pedidos' },
  { pagina: 'gtm',               ruta: '/go-to-market',               titulo: 'Go To Market',              submodulo: 'gtm' },
  /* PÁGINA NUEVA (2026-09-03, pedida por la usuaria) — el PORTAL FTD, réplica
   * de `Farmatodo/FTD_Portal_2.html`: el sell-out de Farmatodo en unidades por
   * tienda, con su resumen, su geografía y sus rankings. Va PRIMERA de la
   * sección porque es la vista general y las otras tres son registros.
   * Audiencia propia y más estrecha que la de la sección: `LISTA_PORTAL_FTD`,
   * diez personas (db/127). */
  { pagina: 'ftd-portal',       ruta: '/portal-ftd',                  titulo: 'Portal FTD',                submodulo: 'farmatodo' },
  { pagina: 'expo-maracaibo',   ruta: '/expobelleza-maracaibo',       titulo: 'Expobelleza Maracaibo',     submodulo: 'farmatodo' },
  { pagina: 'bitftd',           ruta: '/bitacora-ftd',                titulo: 'Bitácora FTD',              submodulo: 'farmatodo' },
  { pagina: 'ncft',             ruta: '/nc-farmatodo',                titulo: 'NC Farmatodo',              submodulo: 'farmatodo' },
  /* PÁGINA NUEVA (2026-08-19, pedida por la usuaria) — Negociaciones
   * Comerciales replicada dentro de la sección Farmatodo, solo para ese
   * cliente. Ver db/040_nego_farmatodo.sql. */
  { pagina: 'nego-ftd',         ruta: '/negociaciones-farmatodo',     titulo: 'Negociaciones Farmatodo',   submodulo: 'farmatodo' },
  { pagina: 'viaticos',          ruta: '/viaticos',                   titulo: 'Viáticos y Gastos',         submodulo: 'viaticos' },
  { pagina: 'inactcli',          ruta: '/inactivacion',               titulo: 'Inactivación Clientes',     submodulo: 'inactcli' },
  { pagina: 'devol',             ruta: '/devoluciones',               titulo: 'Devoluciones',              submodulo: 'devol' },
  { pagina: 'cartera',           ruta: '/cartera',                    titulo: 'Cartera de Clientes',       submodulo: 'cartera' },
  { pagina: 'cart-clientes',     ruta: '/cartera/clientes',           titulo: 'Base de clientes',          submodulo: 'cartera' },
  { pagina: 'cart-historial',    ruta: '/cartera/historial',          titulo: 'Historial de pagos',        submodulo: 'cartera' },
  { pagina: 'cart-cxc',          ruta: '/cartera/cxc',                titulo: 'Cuentas por cobrar',        submodulo: 'cartera' },
  { pagina: 'cart-diascalle',    ruta: '/cartera/dias-calle',         titulo: 'Cobranza en días calle',    submodulo: 'cartera' },
  { pagina: 'cart-cerradas',     ruta: '/cartera/cerradas',           titulo: 'Facturas cerradas del mes', submodulo: 'cartera' },
  { pagina: 'cart-pagos',        ruta: '/cartera/pagos',              titulo: 'Reporte de pago',           submodulo: 'cartera' },
  /* SECCIÓN NUEVA (2026-08-25, pedida por la usuaria) — el porte de la ficha en
   * papel ADF-FOR-012 «FICHA DEL CLIENTE»: el alta de un cliente nuevo desde la
   * visita del asesor hasta el código que asigna Cobranzas. APARTE de Cartera,
   * con entrada propia en el submenú de Cobranzas (como Planificación), y
   * colgada ADEMÁS del módulo Almacén: es lo único que trae a ALMACENYDESPACHO
   * y a FACTURACION, que la usuaria nombró en la audiencia. Ver
   * db/066_prospecto_cliente.sql y 04_DOCS/ESPEC_MODULO_PROSPECTO.md. */
  { pagina: 'prospecto',         ruta: '/prospecto-cliente',          titulo: 'Prospecto Nuevo Cliente',   submodulo: 'prospecto' },
  /* SECCIÓN NUEVA (2026-08-17) — el Planificador de Rutas y Cobranzas de la
   * maqueta PLANIFICACION/, sin subir archivos: lee la CxC viva de Cartera.
   * APARTE de Cartera, con entrada propia en el submenú de Cobranzas (pedido
   * de la usuaria). Ver db/030_planificacion.sql y ESPEC_MODULO_PLANIFICACION.md. */
  { pagina: 'planificacion',     ruta: '/planificacion',              titulo: 'Planificación',             submodulo: 'planificacion' },
  { pagina: 'plan-seguimiento',  ruta: '/planificacion/seguimiento',  titulo: 'Seguimiento Planificación', submodulo: 'planificacion' },
  { pagina: 'plan-cumplimiento', ruta: '/planificacion/cumplimiento', titulo: 'Cumplimiento Planificación', submodulo: 'planificacion' },
  /* SECCIÓN NUEVA (2026-09-30), en PERÍODO DE PRUEBA: la geolocalización de
   * clientes y el marcaje de visitas con GPS (db/192). La ven ocho cuentas por
   * nombre (`src/lib/marcaje-acceso.ts`) y no alimenta ninguna otra sección. */
  { pagina: 'marcaje',           ruta: '/planificacion/marcaje-visita', titulo: 'Marcaje de visita',       submodulo: 'planificacion' },
  /* SECCIÓN NUEVA (2026-10-01), en PERÍODO DE PRUEBA: el REGISTRO de las promos
   * del 4T-2026 que acepta cada cliente en la app, con su aviso y su cierre por
   * Facturación (db/198). Solo MASTER mientras dure la prueba
   * (`src/lib/promos-acceso.ts`). */
  { pagina: 'promos',            ruta: '/comercial/promociones',        titulo: 'Promociones',             submodulo: 'promos' },
  /* SECCIÓN NUEVA (2026-08-31): la planificación MENSUAL del coordinador
   * regional, que sustituye al formato en papel VNT-FOR-013. Cuelga de
   * Comercial, con TRES exclusiones nominales para que los asesores no la vean
   * (db/100_plan_coordinadores.sql). */
  { pagina: 'plan-coord',        ruta: '/comercial/planificacion-coordinadores', titulo: 'Planificación Coordinadores', submodulo: 'plancoord' },
  { pagina: 'an-comparador',     ruta: '/analitica/comparador',       titulo: 'Comparador',                submodulo: 'analitica' },
  { pagina: 'an-pareto',         ruta: '/analitica/pareto',           titulo: 'Pareto ABC & RFM',          submodulo: 'analitica' },
  { pagina: 'an-waterfall',      ruta: '/analitica/puente',           titulo: 'Puente interanual',         submodulo: 'analitica' },
  { pagina: 'an-heatmap',        ruta: '/analitica/estacionalidad',   titulo: 'Estacionalidad',            submodulo: 'analitica' },
  { pagina: 'an-whatif',         ruta: '/analitica/what-if',          titulo: 'Simulador What-If',         submodulo: 'analitica' },
  { pagina: 'ctor',              ruta: '/constructor',                titulo: 'Constructor',               submodulo: '@ctor' },
  /* SECCIÓN NUEVA (2026-08-08) — el material de venta por marca. Desde el
   * 2026-08-17 cuelga de SEIS módulos (tmk, comercial, gerencia, cobranzas,
   * almacen, fact) porque la ve TODO el portal; ver db/039_catalogos_todos.sql. */
  { pagina: 'catalogos',         ruta: '/catalogos',                  titulo: 'Catálogos',                 submodulo: 'catalogos' },
  /* La ADMINISTRACIÓN de catálogos (2026-08-27, pedida por la usuaria para el
   * usuario de Diseño). Cuelga SOLO de `tmk` —el único módulo que tienen a la
   * vez DISEÑO y MASTER— y está excluida para todos los demás roles; ver
   * db/085_catalogos_admin_y_areas.sql y sus controles 5 y 6. */
  { pagina: 'catalogos-admin',   ruta: '/catalogos/administrar',      titulo: 'Administrar',               submodulo: 'catalogos' },
  /* SECCIÓN NUEVA (2026-08-18, pedida por la usuaria) — la PORTADA del módulo
   * Comercial: la infografía de los 7 pasos de la visita. Es además el `landing`
   * de ese módulo en navegacion.ts, así que pulsar «Comercial» entra aquí.
   * Ver db/036_pasos_comerciales.sql. */
  { pagina: 'pasos-comerciales', ruta: '/pasos-comerciales',          titulo: 'Tus 7 pasos comerciales',   submodulo: 'pasos' },
  /* SECCIÓN NUEVA (2026-08-18, pedida por la usuaria) — el Tablero de KPI por
   * vendedor, en C.E.I. (todo + carga del Excel, solo MASTER) y en Comercial
   * (cada asesor SU ficha; cada coordinador su zona). Ver db/037_kpi.sql. */
  { pagina: 'kpis',              ruta: '/kpis',                       titulo: "KPI's",                     submodulo: 'kpis' },
  /* SECCIÓN NUEVA (2026-08-25, pedida por la usuaria) — el Reporte Flash de
   * Ventas (CEI-FOR-005), SOLO en Comercial. Dos hojas carta, una por módulo
   * de empresa. Ver db/061_reporte_flash.sql y 04_DOCS/ESPEC_MODULO_FLASH.md. */
  { pagina: 'flash',             ruta: '/reporte-flash',              titulo: 'Reporte Flash de Ventas',   submodulo: 'flash' },
  /* SECCIÓN NUEVA (2026-08-26, pedida por la usuaria) — el Reporte ACT/INACT
   * Clientes: la hoja `Detalle_Clientes` del libro Acti_Inacti_Clientes_.xlsx
   * que mantiene C.E.I., con su descarga a Excel «con su formato». Cuelga de
   * DOS módulos: `comercial`, que es donde ella pidió la sección, y
   * `cobranzas`, que es lo ÚNICO que trae al usuario COBRANZAS (no tiene
   * Comercial). Ver db/082_act_inact_clientes.sql. */
  { pagina: 'act-inact',         ruta: '/reporte-act-inact',          titulo: 'Reporte ACT/INACT Clientes', submodulo: 'act-inact' },
  /* SECCIÓN NUEVA (2026-08-28, pedida por la usuaria) — Inventario Multimax:
   * la réplica de INVENTARIO_GAMA.xlsx (inventario GA.MA en tiendas Multimax)
   * con sus gráficas y la carga del semanal GAMA.xlsx desde la página. Cuelga
   * de DOS módulos: `comercial`, que es donde ella pidió la sección, y `tmk`,
   * que es lo ÚNICO que trae a TRADEMARKETING (no tiene Comercial). La
   * audiencia son 9 usuarios (db/093): ASESOR319, CENTROLLANOS,
   * GERENCIAVENTAS, TRADEMARKETING, GERENCIACEI y los 4 MASTER. */
  { pagina: 'inv-multimax',      ruta: '/inventario-multimax',        titulo: 'Inventario Multimax',       submodulo: 'invmultimax' },
  /* SECCIÓN NUEVA (2026-09-02, pedida por la usuaria) — la de listas de
   * precios de GA.MA con el nombre disimulado. UNA página con dos pestañas
   * dentro; cuelga solo de `comercial` (db/117). */
  { pagina: 'portafolio',        ruta: '/portafolio-gama',            titulo: 'Portafolio GA.MA',          submodulo: 'portafolio' },
  /* SECCIÓN NUEVA (2026-09-03, pedida por la usuaria) — Gestión de Clientes:
   * el porte del Dashboard de Activación de C.E.I., con sus cinco pestañas y
   * la carga mensual del propio HTML que ella genera. Cuelga solo de
   * `comercial`, y su audiencia son 12 usuarios: gerencia, C.E.I. y los cuatro
   * coordinadores (recortados a su zona). Los ASESORES quedan FUERA a
   * propósito —el tablero los rankea entre sí—, con exclusión de rol en
   * db/128. */
  { pagina: 'gest-clientes',     ruta: '/gestion-clientes',           titulo: 'Gestión de Clientes',       submodulo: 'gestcli' },
  /* SECCIÓN NUEVA (2026-09-25, pedida por la usuaria) — INFORME GESTIÓN
   * COORDINADORES: el VNT-FOR-008 (Índice de Captación de Nuevos Clientes) que
   * completa cada coordinador regional. Fue pestaña de Gestión de Clientes del
   * 23 al 25/09 (db/183) y ella la pidió como sección propia. Cuelga solo de
   * `comercial`, con la misma audiencia de mando que tenía: sin asesores
   * (db/186). */
  { pagina: 'inf-coord',         ruta: '/comercial/informe-gestion-coordinadores', titulo: 'Informe Gestión Coordinadores', submodulo: 'infcoord' },
  { pagina: 'frecuencia',        ruta: '/frecuencia-compra',          titulo: 'Frecuencia de Compra',      submodulo: 'frecuencia' },
  /* SECCIÓN NUEVA (2026-09-22, pedida por la usuaria) — NIVEL DE SERVICIO SLA:
   * la demanda de los consignatarios contra lo realmente despachado, del primer
   * modelo de medición que envió Peter por agosto 2026. Cuelga SOLO de
   * `comercial` y hoy la ve SOLO el rol MASTER: db/178 poda nominalmente los
   * otros NUEVE roles que tienen ese módulo. Nace así a pedido expreso («para
   * esta seccion solo la va a ver por ahora el usuario MASTER»), y el «por
   * ahora» es literal: la sección se abre al Comité cuando ancle al KPI 4. */
  { pagina: 'sla',               ruta: '/nivel-servicio-sla',         titulo: 'Nivel de Servicio SLA',     submodulo: 'sla' },
  /* SECCIÓN NUEVA (2026-08-25, pedida por la usuaria) — **COMITÉS**, con sus
   * TRES subsecciones, SOLO en Comercial. Se llamó «Modelo Diamante» las
   * primeras horas y ella lo renombró esa tarde; las RUTAS conservan
   * `/modelo-diamante/` a propósito (ver el comentario de `navegacion.ts`).
   *
   * El «C. » de los tres títulos es de la usuaria y resuelve una ambigüedad
   * REAL del submenú de Comercial: sin él quedaban dos «Farmatodo» pegados —el
   * comité, sangrado bajo Comités, y el submódulo `farmatodo` que ya existía
   * (Bitácora FTD · NC · Negociaciones FTD)—, distinguibles solo por el nivel.
   * Va SOLO aquí: el `<h1>` de la página lo compone el endpoint y ya dice
   * «Comités · Cuentas Clave», donde repetir la abreviatura sobraría. El corte entre los tres no es a gusto:
   * la hoja «Cadenas y Responsables» marca exactamente tres cadenas con
   * «Continúa manejando su comité de forma independiente» —Farmatodo, Ivoo y
   * Multimax— y esas se van a sus propias páginas. Ver db/068 y
   * 04_DOCS/ESPEC_MODULO_DIAMANTE.md.
   * ⚠️ Tener la página en el token NO basta para verla: el endpoint aplica
   * además el candado de PARTICIPACIÓN (solo quien es responsable de alguna
   * cadena de ese comité, o su coordinador), y `navegacion.ts` lo repite para
   * no ofrecer un enlace que va a dar 403. */
  { pagina: 'diam-cck',          ruta: '/modelo-diamante/cuentas-clave',  titulo: 'C. Cuentas Clave',       submodulo: 'diamante' },
  { pagina: 'diam-ftd',          ruta: '/modelo-diamante/farmatodo',      titulo: 'C. Farmatodo',           submodulo: 'diamante' },
  { pagina: 'diam-ivm',          ruta: '/modelo-diamante/ivoo-multimax',  titulo: 'C. IVOO y Multimax',     submodulo: 'diamante' },
  // MÓDULO NUEVO — no viene del v88. Ver 04_DOCS/ESPEC_MODULO_INFO_INTERES.md
  { pagina: 'info',              ruta: '/informacion-de-interes',     titulo: 'Información de Interés',    submodulo: 'info' },
  { pagina: 'info-admin',        ruta: '/informacion-de-interes/administrar', titulo: 'Administrar',       submodulo: 'info' },
  /* MÓDULO NUEVO (2026-08-21, pedido de la usuaria) — Contraloría, entre Trade
   * Marketing y Constructor. Lo ven MASTER, CONTRALORIA y GERENCIAVENTAS por
   * concesión explícita del módulo, no por exclusión de los demás roles.
   * Ver db/052_bitacora.sql y 04_DOCS/ESPEC_MODULO_BITACORA.md. */
  { pagina: 'bitacora',          ruta: '/contraloria/bitacora',       titulo: 'Bitácora de usuarios',      submodulo: 'contraloria' },
  /* MÓDULO NUEVO (2026-09-01, pedido de la usuaria) — Medios y Comunicación, y
   * su primera sección: Prospectos RRSS, la captación de clientes por las redes
   * sociales de las 5 marcas. Es el porte del libro de consolidación de Kenya
   * Sánchez y de su plantilla de carga. Cuelga SOLO del módulo nuevo, que es lo
   * que evita regalarle la página a AUDIOVISUAL, DISEÑO e I+D — habría pasado
   * colgándola de `tmk`, el único módulo que hoy tiene Medios y Comunicación.
   * Ver db/110_medios_rrss.sql. */
  { pagina: 'rrss',              ruta: '/prospectos-rrss',            titulo: 'Prospectos RRSS',           submodulo: 'rrss' },
];

/** Páginas ya implementadas. El resto cae en <Pendiente/>. */
const IMPLEMENTADAS: Record<string, React.ComponentType> = {
  resumen: Resumen,
  /* Las 3 páginas del Resumen que corren sobre el cubo _RAW (mv_cubo_raw), no
   * sobre el diario: PARIDAD_RESUMEN_3.md. En el v88 estaban MUERTAS (sin
   * ningún botón); aquí se estrenan. FACTURACION no las ve: la poda
   * rol_pagina_excluida se sembró en db/017_resumen3.sql (su candado es de
   * empresa y `descarga` no lo mira — sería una fuga). */
  evolucion: ResumenEvolucion,
  forecast: ResumenForecast,
  descarga: ResumenDescargas,
  'vendedor': Vendedores,
  'vd-evolucion': VendedoresEvolucion,
  'vd-analisis': VendedoresAnalisis,
  'vd-comportamiento': VendedoresComportamiento,
  marcas: Marcas,
  'marca-detail': MarcaDetalle,
  clientes: Clientes,
  'cli-analisis': ClientesAnalisis,
  'cli-maestra': ClientesMaestra,
  'cli-ficha': ClientesFicha,
  'cli-activacion': ClientesActivacion,
  'cli-tendencia': ClientesTendencia,
  // UNA ruta con 7 vistas dentro, no 7 páginas: el RBAC concede el módulo entero.
  financiero: Financiero,
  metas: Metas,
  // Un tablero por empresa (db/246, db/264): la misma página sobre DDH, DHV y DIF.
  pquery: PowerQuery,
  'pquery-dhv': PowerQueryDhv,
  'pquery-dif': PowerQueryDif,
  /* Cartera son SEIS rutas de lectura sobre un solo componente de reporte —el v88
   * también tiene un solo `render()` con cinco definiciones—, y `/cartera` abre la
   * Base de clientes porque es lo que hace su `navTo`. */
  cartera: Cartera,
  'cart-clientes': CarteraClientes,
  'cart-historial': CarteraHistorial,
  'cart-cxc': CarteraCxc,
  'cart-diascalle': CarteraDiasCalle,
  'cart-cerradas': CarteraCerradas,
  /* Reporte de pago, Fase A (ESPEC_MODULO_CARTPAGOS.md): el panel 1 lee
   * `core.cxc_documento` de verdad; los paneles 2-3 son el PROTOTIPO fiel al
   * v88 (ticket en cliente, nada persiste — la pastilla lo declara). La
   * escritura real es la Fase B y espera las 6 decisiones de la spec §10. */
  'cart-pagos': CarteraPagos,
  /* Planificación de visitas: el plan se GUARDA por (mes, vendedor) en
   * core.plan_visita — la maqueta original lo perdía al recargar. */
  planificacion: Planificacion,
  'plan-seguimiento': PlanSeguimiento,
  /* Cumplimiento de la Planificación (db/166): mide lo que YA está cargado en
   * Seguimiento — no tiene subida propia. UN solo porcentaje, y el cálculo vive
   * en src/lib/cumplimiento.ts, compartido con la maqueta que lo contrató. */
  'plan-cumplimiento': PlanCumplimiento,
  marcaje: MarcajeVisitas,
  promos: Promociones,
  'plan-coord': PlanCoordinadores,
  /* Inactivación Clientes: la SEGUNDA página de escritura (tras Negociaciones).
   * El endpoint recalcula dias_sin_compra y el correlativo del informe en
   * servidor, y el duplicado (cod_cli, fecha_visita) responde 409 ANTES de
   * escribir. El «envío» a Crédito y Cobranza es copiar el texto — el correo
   * real es Fase 6 (ESPEC_MODULO_INACTCLI.md §9.3). */
  inactcli: Inactivacion,
  /* Información de Interés: documentos sobre Netlify Blobs con descarga
   * auditada en auth.auditoria. `info` lista y descarga; `info-admin` (solo
   * MASTER vía rol_pagina_excluida) publica. El blob_key jamás viaja al
   * navegador (ESPEC_MODULO_INFO_INTERES.md). */
  catalogos: Catalogos,
  /* Catálogos · Administrar (db/085): el alta de fichas, la subida por partes y
   * el aviso por correo. Solo DISEÑO y MASTER. El `blob_key` no viaja nunca al
   * navegador, igual que en Información de Interés. */
  'catalogos-admin': CatalogosAdmin,
  /* Tus 7 pasos comerciales: una pieza de arte de la usuaria, nada de datos.
   * Es la portada del módulo Comercial (el `landing` de navegacion.ts). */
  'pasos-comerciales': PasosComerciales,
  /* KPI's: el porte de la maqueta de Gestión_KPI. Las fichas llegan del
   * endpoint YA acotadas por alcance y YA derivadas; el PDF es window.print
   * (cada ficha es una hoja carta exacta, como la maqueta). */
  kpis: Kpis,
  /* Reporte Flash de Ventas: el porte de la maqueta de Gestión_ReporteFlash.
   * Las filas llegan del endpoint YA acotadas por alcance; la página agrega
   * por región y arma el podio como `renderModulo()` de la maqueta, y el PDF
   * es window.print (cada módulo es una hoja carta exacta). */
  flash: ReporteFlash,
  /* Reporte ACT/INACT Clientes: copia VERBATIM de la hoja de C.E.I. Las filas
     llegan del endpoint YA acotadas por alcance; los filtros de la barra son
     comodidad. La descarga replica el libro fuente (`lib/actinact-libro.ts`). */
  'act-inact': ReporteActInact,
  /* Inventario Multimax: réplica de INVENTARIO_GAMA.xlsx. El endpoint agrega
     las dos dinámicas en SQL y aplica los tres candados (página, alcance del
     319, carga solo MASTER/ASESOR319); la página pinta y, para quien puede,
     sube el semanal. */
  'inv-multimax': InventarioMultimax,
  /* Portafolio GA.MA: la sección de listas de precios con el nombre
     disimulado. El endpoint sirve las dos ediciones vigentes y decide quién
     puede publicar; el XLSX lo escribe el navegador y el PDF la función. */
  portafolio: PortafolioGama,
  /* Gestión de Clientes: el Dashboard de Activación de C.E.I. El endpoint
     sirve el corte YA RECORTADO por alcance (al coordinador, su zona) y decide
     quién puede cargar; el lector del HTML entra con `import()` solo cuando se
     elige un archivo. */
  'gest-clientes': GestionClientes,
  /* Informe Gestión Coordinadores (VNT-FOR-008): el endpoint cuenta el real en
     la base de captación y lo sirve YA RECORTADO (al coordinador, sus
     regiones); decide también quién carga la meta y quién escribe A-B-C. */
  'inf-coord': InformeCaptacion,
  /* Frecuencia de Compra: el porte del libro GrupoDH_Ventas_2026.xlsx que la
     usuaria mandaba a la fuerza de ventas. El endpoint RECALCULA sus seis hojas
     desde la facturación (paridad medida al céntimo) y las sirve ya recortadas
     por alcance; el XLSX lo escribe el navegador con el formato del libro. */
  frecuencia: Frecuencia,
  /* Nivel de Servicio SLA: los informes por consignatario que envía Peter, ya
     digeridos. La página los pinta tal cual por cadena y añade la pestaña de
     medición, que es lo que el PDF no trae — el fill rate al lado del nivel
     publicado, porque no son la misma cifra (db/178). */
  sla: NivelServicioSla,
  info: InformacionInteres,
  'info-admin': InformacionInteresAdmin,
  /* Contraloría · Bitácora de usuarios: quién entró, qué registró y cuándo
   * salió, con la web y la app móvil distinguidas. Lee `auth.v_bitacora`, que
   * es `auth.auditoria` —la tabla que ya venía grabando los logins desde el día
   * uno— con las etiquetas puestas. La SALIDA solo consta cuando el usuario
   * pulsa Salir, y la página lo dice en vez de rellenarlo (espec §6). */
  bitacora: Bitacora,
  /* Medios y Comunicación · Prospectos RRSS (db/110): el consolidado con su
     alcance ya aplicado por el servidor, la carga de la plantilla de Excel —el
     lector se baja aparte, SheetJS pesa 424 KB— y el circuito de 15 días. Los
     días y el estado de plazo llegan CALCULADOS del endpoint con el día de
     Caracas: la página no los recalcula con el reloj del equipo. */
  rrss: ProspectosRrss,
  /* Constructor, Entregable 1 (PLAN_MODULO_CONSTRUCTOR.md §10): 4 vistas y 5
   * métricas de volumen sin pronóstico — E2 (forecast) y E3 (facturas) se ven
   * deshabilitados y declarados en pantalla. La receta viaja por POST y el
   * alcance va en SQL también en modo SKU (endurece al v88, §9 rec. 2c). */
  ctor: Constructor,
  /* Analítica sí son SEIS páginas con su propio id de RBAC, al contrario que el
   * Financiero: el v88 las registra una a una en `RENDER['an-*']`. */
  'an-insights': ResumenInsights,
  'an-comparador': AnaliticaComparador,
  'an-pareto': AnaliticaPareto,
  'an-waterfall': AnaliticaPuente,
  'an-heatmap': AnaliticaEstacionalidad,
  'an-whatif': AnaliticaWhatIf,
  /* Bitácora FTD: UNA ruta con la pestaña de CONSULTA. El alta del v88 no está y
   * es la D17 — escribe en `localStorage`, que por la D8 no tiene nada que
   * rescatar, y va a la Fase 6 con `nego` y `cart-pagos`. Ojo: tenerla en
   * `sesion.paginas` no basta para verla, el endpoint aplica además el candado de
   * la D16 (Comercial solo para MASTER/KEYACCOUNTS). */
  /* Portal FTD (db/127): el motor, el lector de .xlsx y las gráficas son los de
   * la maqueta, portados VERBATIM a `src/lib/ftd/` — así las cifras no pueden
   * divergir de las suyas. Tenerla en `sesion.paginas` no basta: el endpoint
   * aplica además `LISTA_PORTAL_FTD`, que es lo único que deja fuera a TI. */
  'ftd-portal': PortalFtd,
  'expo-maracaibo': ExpoMaracaibo,
  bitftd: BitacoraFtd,
  ncft: NcFarmatodo,
  /* Negociaciones Farmatodo: la réplica de `nego` con cliente, vendedor y
   * cubeta fijos (grupo Farmatodo · 103 · FARMATODO). Escribe en la MISMA
   * `core.negociacion` —mismo N° de control, misma anulación— y su catálogo de
   * estrategias es `core.estrategia_ftd`, que crece con la opción «Otros». */
  'nego-ftd': NegoFarmatodo,
  /* Lanzamientos: el landing con sus 6 tarjetas y UNA ruta de detalle con DOS
   * renders que no comparten nada —el de kit lee `LNZ` y el de HD lee `HDX`, con
   * datasets, KPIs y filtros distintos—, igual que `lzOpen` en el monolito. El
   * alcance por región y los siete parches por perfil (gotcha 60) los aplica el
   * endpoint, así que aquí no hay ninguna regla de acceso escondida. */
  lanzamientos: Lanzamientos,
  'lanz-detail': LanzamientoDetalle,
  /* Trade: UNA ruta con las TRES vistas dentro (`tmkView` del v88 conmuta sobre
   * el mismo `<section>`), no tres páginas. Sus cifras son un extracto EXTERNO
   * ya agregado —el tablero presupuestario de Trade Marketing— así que no se
   * derivan de `core.factura_linea` ni se recalculan, como el cubo de Power
   * Query. La descarga de las 4 presentaciones NO está: necesita el almacén de
   * archivos del módulo Información de Interés y se declara en pantalla. */
  trade: Trade,
  /* Trazabilidad: el libro de control de presupuesto de Marketing, sus reportes
     recalculados con las reglas del libro, la revisión de su instructivo y la
     bitácora de versiones cargadas (db/181). */
  'traz-mkt': TrazabilidadMkt,
  /* Control de Pedidos: el libro DSP-FOR-005 de Facturación, sus pedidos con el
     estado deducido y el monto base de su factura, y las guías de despacho que
     salen de él —una por pedido, con el número de su proforma— (db/182). */
  'ctrl-pedidos': ControlPedidos,
  /* Negociaciones: UNA ruta con TRES pestañas —«Nueva solicitud» (con el
   * Simulador de Promociones DENTRO desde el 2026-08-14), «Registro» y
   * «Presupuesto del trimestre»—, que es como `NGR.tab` conmuta sobre la misma
   * página. Es la PRIMERA página de escritura del portal: el endpoint recalcula
   * el `v3` y el saldo del presupuesto al recibir el POST y no se fía de lo que
   * mande el navegador. El circuito de aprobación es Fase 6 (D5): una solicitud
   * que cruza el umbral del asesor se guarda marcada y nada la mueve todavía. */
  nego: Negociaciones,
  /* La réplica de la NC (db/072, 2026-08-26). `paginas/NegociacionesNC.tsx` no
   * tiene código propio: es la MISMA página con `variante="nc"`. Entra por el
   * registro y añade la columna del N° de la nota de crédito, que solo escriben
   * los roles de Facturación — el candado está en el endpoint, no en la página. */
  'nego-nc': NegociacionesNC,
  /* Negociaciones BBK (db/190, 2026-09-29). La MISMA página con `variante="bbk"`
   * y registro PROPIO: solo marca BBK, por SKU vigente y sin presupuesto. */
  'nego-bbk': NegociacionesBBK,
  /* Go-To-Market: UNA ruta con las 8 vistas del v88 como estado interno (D-4).
   * El dataset es un extracto EXTERNO congelado al 2026-07-24 y llega del
   * endpoint YA acotado por alcance (D-5, endurecimiento declarado del v88,
   * que servía los 1.762 clientes a cualquiera con `tmk`). Las evaluaciones y
   * las observaciones —las dos escrituras que el monolito guardaba en
   * localStorage de un iframe— van a la base (D-6). PARIDAD_GTM.md. */
  gtm: GoToMarket,
  /* Viáticos: réplica del PROTOTIPO del v88 (roles simulados, memoria, nada
   * persiste — la pastilla lo declara). El circuito real es Fase 6.
   * Devoluciones: el acta FOR-001 con el backend que el v88 nunca tuvo — su
   * window.storage no existía y el correlativo salía siempre -01; aquí lo
   * emite el servidor y el acta queda en core.devolucion. PARIDAD_VIADEV.md. */
  viaticos: Viaticos,
  devol: Devoluciones,
  /* Prospecto Nuevo Cliente: la ficha ADF-FOR-012 con su circuito de tres
   * pasos (registrar → crear con código / devolver con motivo → reenviar). Los
   * recaudos van a Netlify Blobs y su `archivo_ref` JAMÁS viaja al navegador:
   * la descarga pasa por el endpoint, que revalida sesión y alcance. */
  prospecto: Prospecto,
  /* Modelo Diamante: TRES rutas sobre UN componente (`componentes/
   * ModeloDiamante.tsx`), como Cartera con `ReporteCartera`. Los tres comités
   * son la misma ficha —Contactos, Brief y Comité Mensual KPI— sobre distintas
   * cadenas; lo que cambia es cuáles trae el endpoint y quién entra. El KPI 1
   * se CALCULA de `core.factura_linea` en cada carga (no se guarda) y el KPI 3
   * se ancla al extracto de Go-To-Market. */
  'diam-cck': DiamanteCuentasClave,
  'diam-ftd': DiamanteFarmatodo,
  'diam-ivm': DiamanteIvooMultimax,
};

/**
 * A DÓNDE IBA EL USUARIO ANTES DE TROPEZAR CON EL ACCESO (2026-09-08).
 *
 * Los avisos del portal mandan enlaces PROFUNDOS —`/negociaciones?nego=148&
 * tipo=VENTAS` es el de las cuatro notificaciones de Negociaciones— y quien los
 * pulsa casi nunca tiene la sesión abierta: llega del correo, en otro equipo o
 * pasadas las 8 h que dura la cookie. Sin esto, el comodín de la rama sin sesión
 * se lo llevaba a `/login` a secas: **la query se perdía ahí mismo** y, al
 * entrar, `/login` lo mandaba a su página de inicio. Es el mismo fallo que db/106
 * arregló para el enlace de la clave, con la diferencia de que aquí no hay una
 * ruta propia que reconocer — hay 68.
 *
 * Se lleva en la URL y no en el `state` del historial a propósito: se ve, se
 * puede pegar en un ticket, y sobrevive a un F5 en la pantalla de acceso.
 */
export const conDestino = (ruta: string): string =>
  ruta === '/' || ruta.startsWith('/login')
    ? '/login'
    : `/login?destino=${encodeURIComponent(ruta)}`;

/**
 * El destino guardado, o la página de inicio. Solo se acepta una ruta INTERNA:
 * `//otro-sitio.com` es una URL absoluta para el navegador aunque empiece por
 * barra, y dejarla pasar convertiría el acceso del portal en un redirector
 * abierto — el tipo de enlace con el que se hacen las suplantaciones. Si el
 * destino ya no existe o el perfil no lo ve, el comodín de más abajo lo recoge
 * y lo deja en su inicio, que es lo que pasaba antes de este cambio.
 */
export const trasEntrar = (busca: string, inicio: string): string => {
  const d = new URLSearchParams(busca).get('destino') ?? '';
  return d.startsWith('/') && !d.startsWith('//') && !d.startsWith('/login') ? d : inicio;
};

export function Rutas() {
  const { sesion, cargando, puedeVer } = useSesion();
  const donde = useLocation();

  if (cargando) return <div className="p-8 text-sm opacity-70">Cargando…</div>;

  if (!sesion) {
    return (
      /* El límite envuelve al Suspense, no al revés: lo que hay que recoger es
       * justo lo que el `lazy()` lanza DENTRO de él. */
      <LimiteDeError>
      <Suspense fallback={null}>
        <Routes>
          <Route path="/login" element={<Login />} />
          {/* db/106 · El enlace del correo. Va ANTES del comodín o el `Navigate`
            * se lo lleva al acceso con el token puesto en una URL que ya nadie
            * mira — el usuario vería la pantalla de entrar y creería que el
            * enlace no funciona. `RUTA_CLAVE_NUEVA` de `src/lib/enlace-clave.ts`
            * es la MISMA constante con la que el servidor arma el correo. */}
          <Route path={RUTA_CLAVE_NUEVA} element={<ClaveNueva />} />
          {/* Con el destino puesto: ver `conDestino`. Vale igual para la sesión
            * caducada — `sesion` se vuelve `null` sin navegar a ninguna parte,
            * así que la ruta que se pierde es la que el usuario tenía abierta. */}
          <Route path="*" element={<Navigate to={conDestino(donde.pathname + donde.search)} replace />} />
        </Routes>
      </Suspense>
      </LimiteDeError>
    );
  }

  const permitidas = RUTAS.filter((r) => puedeVer(r.pagina));
  // La ruta de inicio nunca puede ser una con parámetro (`:id`).
  const inicio = permitidas.find((r) => !r.oculta)?.ruta ?? '/sin-acceso';

  return (
    <LimiteDeError>
    <Suspense fallback={<div className="p-8 text-sm opacity-70">Cargando módulo…</div>}>
      <Routes>
        {/* Ya con sesión: si venía de un enlace del correo, se le devuelve ESA
          * página —con su query— en vez de su inicio. `Login` no navega; monta
          * la sesión y este `Navigate` es el que decide. Ver `trasEntrar`. */}
        <Route path="/login" element={<Navigate to={trasEntrar(donde.search, inicio)} replace />} />
        {/* db/106 · También CON sesión: quien tenga el portal abierto en el
          * escritorio y pulse el enlace del correo tiene que ver la pantalla de
          * reponer clave, no su tablero. `Marco` la deja pintarse sin marco. */}
        <Route path={RUTA_CLAVE_NUEVA} element={<ClaveNueva />} />
        {permitidas.map((r) => {
          const Comp = IMPLEMENTADAS[r.pagina] ?? Pendiente;
          return <Route key={r.pagina} path={r.ruta} element={<Comp />} />;
        })}
        {/* El simulador fue página propia UN día (13→14 de agosto): quien tenga
          * la URL guardada cae en Negociaciones, donde vive ahora. Si el perfil
          * no ve `nego`, el catch-all lo recoge en el segundo salto. */}
        <Route path="/negociaciones/simulador"
          element={<Navigate to="/negociaciones" replace />} />
        {/* Insights vivió en Analítica hasta el 2026-08-21 (db/050). Quien tenga
          * la URL vieja guardada —o la reciba en un correo anterior— cae en la
          * nueva; sin esto, el catch-all lo mandaría a su inicio sin decir nada
          * y parecería que la página desapareció. */}
        <Route path="/analitica/insights"
          element={<Navigate to="/resumen/insights" replace />} />
        <Route path="/sin-acceso" element={
          <div className="p-8">
            <h1 className="text-lg font-semibold">Sin módulos asignados</h1>
            <p className="mt-2 text-sm opacity-70">
              Tu usuario no tiene ningún módulo habilitado. Contacta a Control Estadístico.
            </p>
          </div>
        } />
        <Route path="*" element={<Navigate to={inicio} replace />} />
      </Routes>
    </Suspense>
    </LimiteDeError>
  );
}
